// Pill-style add-to-cart for product cards.
// Loaded as an external `defer` script so it runs after the full document is
// parsed — avoiding the classic custom-element upgrade race where
// connectedCallback fires before child elements are parsed.

(() => {
  if (customElements.get('ztove-card-atc')) return;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class ZtoveCardAtc extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      // Defer wiring to a microtask so children are guaranteed to be parsed
      // even if the element happens to be upgraded mid-parse.
      queueMicrotask(() => this.wire());
    }

    wire() {
      if (this._wired) return;
      this._wired = true;

      this.variantId = Number(this.getAttribute('data-variant-id') || 0);
      this.formHost = this.querySelector('.ztove-card-atc__form-host');
      this.btn = this.querySelector('.ztove-card-atc__btn');
      this.stepper = this.querySelector('[data-stepper]');
      this.valueEl = this.querySelector('[data-qty-value]');
      this.decBtn = this.querySelector('[data-decrease]');
      this.incBtn = this.querySelector('[data-increase]');
      this.qty = Number(this.getAttribute('data-initial-qty') || 0);
      this.form = this.querySelector('form[action*="/cart/add"]');

      if (this.form) {
        this.form.addEventListener('submit', () => {
          if (this.btn) this.btn.classList.add('is-loading');
          this.pollCartUntilAdded(3000);
        });
      }

      if (this.incBtn) {
        this.incBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.onChange(1);
        });
      }
      if (this.decBtn) {
        this.decBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.onChange(-1);
        });
      }

      this.addEventListener('click', (e) => {
        if (e.target instanceof Element && e.target.closest('button')) {
          e.stopPropagation();
        }
      });

      this._cartListener = (event) => this.onCartUpdate(event);
      document.addEventListener('cart:update', this._cartListener);

      // Reconcile against the live cart once on mount. The page may be
      // cached with a stale `initial_qty` (e.g. user cleared cart in another
      // tab, or Shopify's section cache lagged behind cart state). Without
      // this, the SSR can show the stepper at qty:1 even when /cart.js
      // returns 0 items — confusing the customer.
      this.refetchAndSync();
    }

    disconnectedCallback() {
      if (this._cartListener) document.removeEventListener('cart:update', this._cartListener);
    }

    showStepper(quantity) {
      this.qty = quantity;
      if (this.valueEl) this.valueEl.textContent = String(quantity);
      if (this.btn) this.btn.classList.remove('is-loading');
      if (this.formHost) this.formHost.classList.add('is-hidden');
      if (this.stepper) this.stepper.classList.remove('is-hidden');
    }

    showButton() {
      this.qty = 0;
      if (this.btn) this.btn.classList.remove('is-loading');
      if (this.formHost) this.formHost.classList.remove('is-hidden');
      if (this.stepper) this.stepper.classList.add('is-hidden');
    }

    async pollCartUntilAdded(timeoutMs) {
      const startQty = this.qty;
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 350));
        try {
          const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
          if (!res.ok) continue;
          const cart = await res.json();
          const line = (cart.items || []).find((item) => Number(item.variant_id) === this.variantId);
          const liveQty = (line && line.quantity) || 0;
          if (liveQty > startQty) {
            this.showStepper(liveQty);
            return;
          }
        } catch (_) {
          /* keep polling */
        }
      }
      if (this.btn) this.btn.classList.remove('is-loading');
    }

    onChange(delta) {
      if (!this.variantId) return;

      // Optimistic UI: update value immediately so rapid clicks feel
      // responsive. The actual cart write is serialized below so that
      // multiple back-to-back clicks don't race each other on the server.
      const nextQty = Math.max(0, (this.qty || 0) + delta);
      if (nextQty === 0) {
        this.showButton();
      } else {
        this.qty = nextQty;
        if (this.valueEl) this.valueEl.textContent = String(nextQty);
      }
      if (this.valueEl) this.valueEl.classList.add('is-changing');

      // Queue write behind any in-flight write so server-side order matches
      // click order. Without this, parallel /cart/update.js calls can land
      // out of order and leave the cart at the wrong quantity.
      this._writeQueue = (this._writeQueue || Promise.resolve())
        .catch(() => {})
        .then(() => this.writeQuantity(nextQty));
    }

    async writeQuantity(targetQty) {
      const updates = {};
      updates[String(this.variantId)] = targetQty;

      // Include cart-items section ids so the response carries pre-rendered
      // HTML for the cart drawer. cart-items-component listens for
      // `cart:update` and morphs its section using detail.data.sections.
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      const body = { updates };
      if (sectionIds.length) body.sections = sectionIds.join(',');

      try {
        const res = await fetch(`${root()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error('cart update failed');
        const cart = await res.json();

        const line = (cart.items || []).find((i) => Number(i.variant_id) === this.variantId);
        const liveQty = (line && line.quantity) || 0;
        if (liveQty === 0) {
          this.showButton();
        } else {
          this.qty = liveQty;
          if (this.valueEl) this.valueEl.textContent = String(liveQty);
        }

        // Tell cart-items-component (and friends) to refresh, but suppress
        // the cart drawer's auto-open. cart-drawer-component opens on every
        // `cart:update` if it has the `auto-open` attribute — we don't want
        // a +/- click to pop the drawer.
        const openers = Array.from(document.querySelectorAll('cart-drawer-component[auto-open]'));
        openers.forEach((d) => d.removeAttribute('auto-open'));
        try {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: 'ztove-card-atc',
                data: {
                  source: 'ztove-card-atc',
                  sections: cart.sections,
                  itemCount: cart.item_count,
                  variantId: String(this.variantId),
                },
              },
            })
          );
        } finally {
          // Restore on next tick so any async listeners that look at the
          // attribute don't see a stale state.
          setTimeout(() => openers.forEach((d) => d.setAttribute('auto-open', '')), 0);
        }
      } catch (err) {
        console.error('[ztove-card-atc] change failed', err);
        this.refetchAndSync();
      } finally {
        if (this.valueEl) this.valueEl.classList.remove('is-changing');
      }
    }

    async refetchAndSync() {
      try {
        const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        const line = (cart.items || []).find((i) => Number(i.variant_id) === this.variantId);
        const liveQty = (line && line.quantity) || 0;
        if (liveQty === 0) {
          this.showButton();
        } else {
          this.qty = liveQty;
          if (this.valueEl) this.valueEl.textContent = String(liveQty);
          if (this.formHost) this.formHost.classList.add('is-hidden');
          if (this.stepper) this.stepper.classList.remove('is-hidden');
        }
      } catch (_) {
        /* no-op */
      }
    }

    onCartUpdate(event) {
      const detail = event && event.detail;
      if (!detail) return;
      const cart = detail.resource;
      if (!cart || !Array.isArray(cart.items)) return;

      if (detail.sourceId === 'ztove-card-atc' && String(detail.data && detail.data.variantId) === String(this.variantId)) return;

      const line = cart.items.find((item) => Number(item.variant_id) === this.variantId);
      const liveQty = (line && line.quantity) || 0;

      if (liveQty > 0 && this.qty !== liveQty) {
        this.showStepper(liveQty);
      } else if (liveQty === 0 && this.qty > 0) {
        this.showButton();
      }
    }
  }

  customElements.define('ztove-card-atc', ZtoveCardAtc);
})();
